import React, { useState } from 'react';
import SectionCard from './SectionCard.jsx';
import DensityChip from './DensityChip.jsx';

/* NestedSection — wraps dense lists with progressive disclosure.
   Levels:
     L0 — headline (always visible)
     L1 — top drivers (hidden in 'sparse', visible in 'dense' + 'comfortable')
     L2 — full list (hidden until "Show all (N)" or 'comfortable')

   Density resolution: scenario.sectionDensity[sectionId] || densityDefault || 'dense'.
*/
export default function NestedSection({
  sectionId,
  title,
  subtitle,
  scenario,
  setScenario,
  headline,                  // <ReactNode> — always visible (L0)
  topDrivers,                // <ReactNode> — L1 (top 3 drivers)
  fullList,                  // <ReactNode> — L2 (everything)
  fullCount,                 // optional total row count for the "Show all (N)" affordance
  densityDefault = 'dense',
  right,                     // optional extra header actions
}) {
  const persisted = scenario?.sectionDensity?.[sectionId];
  const density = persisted || densityDefault;
  const [showAll, setShowAll] = useState(false);

  const setDensity = (next) => {
    if (!setScenario) return;
    const cur = scenario.sectionDensity || {};
    setScenario({ ...scenario, sectionDensity: { ...cur, [sectionId]: next } });
    if (next !== 'comfortable') setShowAll(false);
  };

  const showL1 = density !== 'sparse' && !!topDrivers;
  const showL2 = density === 'comfortable' || (density === 'dense' && showAll);

  const expandBtn = 'self-start bg-transparent border border-dashed border-border-strong text-text-muted px-[0.7rem] py-[0.35rem] rounded-sm cursor-pointer text-[0.75rem] font-[inherit] hover:text-text hover:border-accent-gold';

  return (
    <SectionCard
      title={title}
      subtitle={subtitle}
      right={
        <div className="flex gap-[0.4rem] items-center">
          <DensityChip value={density} onChange={setDensity} />
          {right}
        </div>
      }
    >
      <div className="flex flex-col gap-[0.85rem]" data-section={sectionId} id={`section-${sectionId}`}>
        {headline && <div className="flex flex-col gap-2">{headline}</div>}

        {showL1 && (
          <div className="flex flex-col gap-2 border-t border-dashed border-border pt-[0.7rem]">
            {topDrivers}
          </div>
        )}

        {fullList && (
          <>
            {density === 'dense' && !showAll && fullCount > 0 && (
              <button
                type="button"
                className={expandBtn}
                onClick={() => setShowAll(true)}
              >
                Show all ({fullCount}) ▾
              </button>
            )}
            {showL2 && (
              <div className="flex flex-col gap-2 border-t border-dashed border-border pt-[0.7rem]">
                {fullList}
                {density === 'dense' && showAll && (
                  <button
                    type="button"
                    className={expandBtn}
                    onClick={() => setShowAll(false)}
                  >
                    Collapse ▴
                  </button>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </SectionCard>
  );
}
